package com.taocent.simple.compose.component.richtext.core.internal.ui

import androidx.compose.ui.platform.Clipboard
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import com.taocent.simple.compose.component.richtext.core.RichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.clipboardGetText
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.clipboardSetText
import com.taocent.simple.compose.component.richtext.core.internal.serialization.RichTextHtmlConfig

/**
 * Emoji shortcode `[id]` 匹配 — 提到 file-level 常量,避免每次 paste 都重新编译 regex。
 * Chrome V8 不接受 `[...+]` 字符类闭合 `]` 紧跟量化符的 regex 字面,所以用 raw string + escape。
 *
 * 与 [com.taocent.simple.compose.component.blockrichtext.internal.platform.ImePasteInterceptor.containsValidEmojiShortcode]
 * 共用同一 pattern。
 */
private val EMOJI_SHORTCODE_REGEX = Regex("""\[([a-zA-Z0-9_]+)\]""")

/**
 * Paste 决策 + 应用的单一入口,抽到 top-level 便于多个 paste 入口共享
 * (Cmd/Ctrl+V 拦截器、IME 拦截器 web 端、浮动工具栏 onPaste 等)。
 *
 * 跨模块共享(从 :richtext-core 暴露到 :blockrichtext),所以**不**是 internal。
 * 内部 :richtext-core 单一调用方是 [RichTextClipboardActions.paste],但
 * :blockrichtext 的 web 端 capture paste event listener / 浮动工具栏 onPaste
 * 等入口也复用本决策链。
 *
 * 决策优先级(与 [com.taocent.simple.compose.component.blockrichtext.internal.platform.ImePasteInterceptor.handleImePasteCandidate]
 * 在 common 端保持一致,便于阅读):
 *   0. **[onBeforePreload] 命中** → 调用方处理(块级:`pasteJson` 整体替换)。
 *   1. 文本含富文本 spans / paragraph styles(由 `hasRichStyles` 判定)
 *      → [RichTextState.insertAnnotatedString] 还原 spans。
 *      注:之前的 `resolveClipboard` 进程内 buffer 命中路径已移除,统一依赖
 *      上游 `clipboardGetText` 读 system clipboard 的 text/html 反序列化结果。
 *   2. 文本含 `[emojiId]` 形式且 id 在 [RichTextConfig.customEmojis] 中
 *      → [RichTextState.insertTextWithEmojis] 还原 emoji 渲染。
 *   3. [RichTextConfig] 启用 smart paste + [allowSmartPaste] = true
 *      → [RichTextState.smartPaste] 走 HTML / Markdown / JSON 解析。
 *   4. fallback → [RichTextState.insertText] 插入纯文本。
 *
 * `rawClipAnnotated` 可能为 null(剪贴板为空 / 读取失败)。
 *
 * [onBeforePreload] 是 0 级回调:在所有"标准 paste 决策"之前先调用一次,返回 true
 * 表示"已被消费,跳过后续 1-4 优先级"。`:blockrichtext` 用此机制注入 Block JSON
 * 整体替换(`{ state.pasteJson(it); true }`);`:richtext` 调用方传 null,行为完全
 * 等同于改前。
 */
suspend fun applyPreloadedPaste(
    state: RichTextState,
    rawClipAnnotated: AnnotatedString?,
    config: RichTextConfig,
    allowSmartPaste: Boolean,
    /**
     * 0 级 paste 决策 hook,签名 `(String) -> Boolean`。
     *
     * - 参数:剪贴板 plain text(`clipAnnotated.text`)。
     * - 返回 true:表示"已被消费",`applyPreloadedPaste` 直接 return,不再走后续 1-4 优先级。
     * - 返回 false 或传 null:继续走 1-4 优先级。
     *
     * 用于 :blockrichtext 注入 Block JSON 整体替换;普通富文本传 null。
     */
    onBeforePreload: (suspend (String) -> Boolean)? = null,
) {
    val clipAnnotated = rawClipAnnotated
    if (clipAnnotated == null || clipAnnotated.text.isEmpty()) return
    val clipText = clipAnnotated.text
    // 0 级 hook(块级:pasteJson 整体替换)
    if (onBeforePreload?.invoke(clipText) == true) return
    val hasRichStyles = clipAnnotated.spanStyles.isNotEmpty() ||
        clipAnnotated.paragraphStyles.any { it.item.textAlign != TextAlign.Unspecified }
    if (hasRichStyles) {
        state.insertAnnotatedString(clipAnnotated)
        return
    }
    // emoji id Set 提前到这里 — `customEmojis.map { it.id }.toSet()` 在每次 paste
    // 都重建,提到局部变量后只构造一次(优先短路:`customEmojis` 空时直接跳过 regex 扫描)
    val customEmojis = config.customEmojis
    if (customEmojis.isNotEmpty()) {
        val validEmojiIds = customEmojis.mapTo(HashSet(customEmojis.size)) { it.id }
        val hasEmojiShortcode = EMOJI_SHORTCODE_REGEX.findAll(clipText).any { match ->
            match.groupValues[1] in validEmojiIds
        }
        if (hasEmojiShortcode) {
            val displaySize = state.currentFontSize
                .takeIf { it != TextUnit.Unspecified }
                ?: 16.sp
            state.insertTextWithEmojis(clipText, validEmojiIds, displaySize)
            return
        }
    }
    val anySmartPasteEnabled = config.smartPasteJsonEnabled ||
        config.smartPasteHtmlEnabled ||
        config.smartPasteMarkdownEnabled
    if (allowSmartPaste && anySmartPasteEnabled) {
        state.smartPaste(
            clipText,
            jsonEnabled = config.smartPasteJsonEnabled,
            htmlEnabled = config.smartPasteHtmlEnabled,
            markdownEnabled = config.smartPasteMarkdownEnabled,
            parsers = config.pasteParsers
        )
    } else {
        state.insertText(clipText)
    }
}

class RichTextClipboardActions(
    private val clipboard: Clipboard,
    private val state: RichTextState,
    private val config: RichTextConfig,
    /**
     * 序列化到系统剪贴板 text/html 副 MIME 时的配置(决定 [com.taocent.simple.compose.component.richtext.core.internal.serialization.AnnotatedStringToHtml]
     * 用语义标签还是 `<span style>`、是否包 `<div>` 等)。默认 [RichTextHtmlConfig] 即可,
     * 调用方如需覆盖(例如跨 app 粘贴到外部 HTML 渲染器时),可在构造时传入。
     *
     * 只影响写剪贴板(`copySelection` / `cutSelection`)时是否带 text/html 副 MIME + 用什么
     * 风格序列化;不影响读剪贴板(`paste`) —— 读取走 [com.taocent.simple.compose.component.richtext.core.internal.serialization.HtmlToAnnotatedString.parse]
     * 单向解析。
     */
    private val htmlConfig: RichTextHtmlConfig = RichTextHtmlConfig(),
) {
    suspend fun copySelection() {
        val selected = selectedText() ?: return
        clipboardSetText(clipboard, state, selected, htmlConfig)
    }

    suspend fun cutSelection() {
        val selected = selectedText() ?: return
        clipboardSetText(clipboard, state, selected, htmlConfig)
        state.deleteSelection()
    }

    /**
     * 公开 paste 入口:从系统剪贴板读取后调 [applyPreloadedPaste] 应用 paste 逻辑。
     */
    suspend fun paste(allowSmartPaste: Boolean) {
        val rawClipAnnotated = clipboardGetText(clipboard, state, htmlConfig).getOrNull()
        applyPreloadedPaste(state, rawClipAnnotated, config, allowSmartPaste)
    }

    /**
     * 重载:由调用方(如 [com.taocent.simple.compose.component.blockrichtext.BlockRichTextEditor]
     * 顶层 Cmd/Ctrl+V 拦截器)**预先读取**剪贴板后传入,跳过内部 [clipboardGetText] 的
     * 第二次异步读取 —— 在 web 端特别关键,因为 `navigator.clipboard.read()` 走 IPC
     * + permission check,是 web 端 paste 性能的最大头。调用方应承担"读取剪贴板"的开销,
     * 本方法只负责 paste 决策 + 应用。
     *
     * 传入 `null` 表示"剪贴板为空 / 读取失败",等价于 [paste] 的空剪贴板分支。
     *
     * [onBeforePreload] 同样支持回调注入,块级传 `pasteJson` 整体替换。
     */
    suspend fun paste(
        allowSmartPaste: Boolean,
        preloadedClipAnnotated: AnnotatedString?,
        onBeforePreload: (suspend (String) -> Boolean)? = null,
    ) {
        applyPreloadedPaste(state, preloadedClipAnnotated, config, allowSmartPaste, onBeforePreload)
    }

    private fun selectedText(): AnnotatedString? {
        val selection = state.textFieldValue.selection
        if (selection.collapsed) return null
        return state.textFieldValue.annotatedString.subSequence(selection.min, selection.max)
    }
}
